<template>
  <v-container fluid class="disponibilidad-page pa-3">
    <v-row no-gutters>
      <v-col cols="12" class="d-flex flex-column">
        <v-card class="availability-card d-flex flex-column" elevation="2">
          <v-card-title class="d-flex align-center flex-wrap flex-shrink-0">
            <span class="text-h6">Disponibilidad de colaboradores</span>
            <v-chip v-if="fechasVisibles.length" small class="ml-3" color="primary" text-color="white">
              {{ rangoTexto }}
            </v-chip>
            <v-spacer />
            <div class="d-flex align-center flex-wrap">
              <v-btn v-if="puedeGestionarDisponibilidad" color="primary" @click="abrirRegistrarAusencia()">
                <v-icon left small>mdi-calendar-plus</v-icon>
                Registrar ausencia
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text class="pa-0 flex-grow-1 d-flex flex-column overflow-hidden table-card-text">
            <v-alert v-if="!periodosAusencia.length && !loading" type="info" dense class="ma-3 flex-shrink-0">
              {{ textoSinAusencias }}
            </v-alert>
            <div class="table-wrapper">
              <table v-if="fechasVisibles.length" class="availability-table">
                <thead>
                  <tr>
                    <th class="col-area">Área</th>
                    <th class="col-nombre">Nombre</th>
                    <th class="col-backup">Backup / Proyecto</th>
                    <th v-for="f in fechasVisibles" :key="f" class="col-fecha"
                      :class="{ 'fecha-hoy': esHoy(f), 'col-finde': esFinDeSemana(f) }">
                      <span class="fecha-label">{{ formatearFechaCorta(f) }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in colaboradoresFiltradosOrdenados" :key="c.id" class="row-collaborator"
                    :class="{ 'row-clickable': puedeGestionarDisponibilidad }"
                    @click="puedeGestionarDisponibilidad && abrirEditarAusencia(c)">
                    <td class="col-area">{{ c.area }}</td>
                    <td class="col-nombre">{{ c.nombre }}</td>
                    <td class="col-backup">{{ backupDisplay(c) }}</td>
                    <td v-for="f in fechasVisibles" :key="f" class="col-fecha cell-day"
                      :class="[claseCelda(c.dias[f]), { 'col-finde': esFinDeSemana(f) }]"
                      :title="tituloCelda(c.dias[f], f)">
                      {{ c.dias[f] || "" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="legend pa-3 flex-shrink-0 d-flex align-center flex-wrap">
              <span class="legend-title">Leyenda:</span>
              <span class="legend-item va">VA = Vacaciones</span>
              <span class="legend-item te">TE = Terreno</span>
              <span class="legend-item lm">LM = Licencia Médica</span>
              <span class="legend-item tt">TT = TeleTrabajo</span>
              <v-spacer />
              <v-btn
                icon
                color="green darken-2"
                title="Exportar a Excel"
                @click="exportarExcel"
                :disabled="!colaboradoresFiltradosOrdenados.length || !fechasVisibles.length"
              >
                <v-icon>mdi-file-excel</v-icon>
              </v-btn>
            </div>
          </v-card-text>

          <v-alert v-if="error" type="error" dense class="ma-3 flex-shrink-0">{{ error }}</v-alert>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo: Registrar / Editar ausencia -->
    <v-dialog v-model="dialogAusencia" max-width="500" persistent>
      <v-card>
        <v-card-title>{{ editingPeriodIndex !== null ? "Editar periodo de ausencia" : "Registrar periodo de ausencia"
          }}</v-card-title>
        <v-card-text>
          <v-autocomplete v-model="formColaboradorId" :items="colaboradoresVigentesParaSelect" item-text="nombre"
            item-value="id" label="Colaborador" outlined dense clearable class="mb-3"
            placeholder="Escribe para buscar..." />
          <v-menu ref="menuDesde" v-model="menuDesde" :close-on-content-click="false" transition="scale-transition"
            offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="formDesde" label="Desde" outlined dense readonly v-bind="attrs" v-on="on"
                class="mb-3" />
            </template>
            <v-date-picker v-model="formDesde" no-title scrollable locale="es-cl" @input="menuDesde = false" />
          </v-menu>
          <v-menu ref="menuHasta" v-model="menuHasta" :close-on-content-click="false" transition="scale-transition"
            offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="formHasta" label="Hasta" outlined dense readonly v-bind="attrs" v-on="on"
                class="mb-3" />
            </template>
            <v-date-picker v-model="formHasta" no-title scrollable locale="es-cl" @input="menuHasta = false" />
          </v-menu>
          <v-autocomplete v-model="formMotivo" :items="motivosAusencia" item-text="text" item-value="value"
            label="Motivo de ausencia" outlined dense class="mb-3" placeholder="Escribe para buscar...">
            <template v-slot:item="{ item }">
              <span :class="'motivo-color-' + item.value">{{ item.text }}</span>
            </template>
            <template v-slot:selection="{ item }">
              <span :class="'motivo-color-' + item.value">{{ item.text }}</span>
            </template>
          </v-autocomplete>
          <v-autocomplete v-model="formBackupIds" :items="colaboradoresParaBackup" item-text="nombre" item-value="id"
            label="Backup (máximo 3)" outlined dense multiple chips small-chips clearable
            hint="Selecciona hasta 3 colaboradores como backup" persistent-hint class="mb-2"
            placeholder="Escribe para buscar..." @input="truncarBackupIds" />
          <v-autocomplete
            v-model="formProyectoId"
            :items="proyectos"
            item-text="project_code"
            item-value="id"
            label="Proyecto (BD gabinetes)"
            outlined
            dense
            clearable
            placeholder="Buscar en proyectos de gabinetes..."
            class="mb-2"
            :loading="loadingProyectos"
            hint="Elige de la lista o escribe abajo el proyecto manualmente"
            persistent-hint
            @input="formProyectoTexto = ''"
          >
            <template v-slot:item="{ item }">
              <span class="font-weight-medium">{{ item.project_code }}</span>
              <span v-if="item.description" class="ml-2 grey--text text--darken-1">— {{ item.description }}</span>
            </template>
            <template v-slot:selection="{ item }">
              <span>{{ item.project_code }}</span>
            </template>
          </v-autocomplete>
          <v-text-field
            v-model="formProyectoTexto"
            label="O escribir proyecto manualmente"
            outlined
            dense
            clearable
            placeholder="Código o nombre del proyecto"
            class="mb-2"
            hint="Si no está en la BD de gabinetes, escríbelo aquí"
            persistent-hint
            @input="formProyectoId = null"
          />
          <v-alert v-if="errorForm" type="error" dense class="mt-2">{{ errorForm }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="editingPeriodIndex !== null" text color="error" :disabled="guardando"
            @click="eliminarPeriodoAusencia">
            Eliminar periodo
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="guardando" @click="cerrarDialogAusencia">Cancelar</v-btn>
          <v-btn color="primary" :loading="guardando" @click="guardarAusencia">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import API_BASE from "@/helpers/api";
import ExcelJS from "exceljs";

export default {
  name: "DisponibilidadView",
  data() {
    const hoy = new Date().toISOString().slice(0, 10);
    return {
      loading: false,
      error: "",
      colaboradores: [],
      fechas: [],
      filtroTexto: "",
      dialogAusencia: false,
      formColaboradorId: null,
      formDesde: hoy,
      formHasta: hoy,
      formMotivo: null,
      formBackupIds: [],
      formProyectoId: null,
      formProyectoTexto: "",
      proyectos: [],
      loadingProyectos: false,
      menuDesde: false,
      menuHasta: false,
      errorForm: "",
      motivosAusencia: [
        { value: "LM", text: "Licencia médica (LM)" },
        { value: "VA", text: "Vacaciones (VA)" },
        { value: "TE", text: "Terreno (TE)" },
        { value: "TT", text: "TeleTrabajo (TT)" },
      ],
      periodosAusencia: [],
      editingPeriodIndex: null,
      guardando: false,
      maxDiasVisibles: 21,
    };
  },
  computed: {
    usuario() {
      try {
        return JSON.parse(
          sessionStorage.getItem("usuario") || localStorage.getItem("usuario") || "null"
        );
      } catch (e) {
        return null;
      }
    },
    puedeGestionarDisponibilidad() {
      return this.tieneRol(["admin", "adminTI"]);
    },
    textoSinAusencias() {
      return this.puedeGestionarDisponibilidad
        ? 'No hay periodos de ausencia. Usa "Registrar ausencia" para agregar uno; el calendario mostrará solo los días con ausencias.'
        : 'No hay periodos de ausencia en el rango visible.';
    },
    colaboradoresVigentesParaSelect() {
      return (this.colaboradores || []).filter((c) => this.esVigente(c));
    },

    colaboradoresParaBackup() {
      const vigentes = (this.colaboradores || []).filter((c) => this.esVigente(c));
      const idSel = this.formColaboradorId;
      if (!idSel) return vigentes;
      return vigentes.filter((c) => Number(c.id) !== Number(idSel));
    },
    rangoDesdeHasta() {
      const p = this.periodosAusencia || [];
      if (p.length === 0) return null;
      const toYMD = (val) => {
        if (val == null) return val;
        if (typeof val === "string" && val.length >= 10 && val[4] === "-" && val[7] === "-") return val.slice(0, 10);
        try {
          const d = new Date(val);
          return isNaN(d.getTime()) ? val : d.toISOString().slice(0, 10);
        } catch (e) {
          return String(val).slice(0, 10);
        }
      };
      let desde = toYMD(p[0].desde);
      let hasta = toYMD(p[0].hasta);
      p.forEach((period) => {
        const pd = toYMD(period.desde);
        const ph = toYMD(period.hasta);
        if (pd && pd < desde) desde = pd;
        if (ph && ph > hasta) hasta = ph;
      });
      return { desde, hasta };
    },
    fechasVisibles() {
      const r = this.rangoDesdeHasta;
      const hoy = new Date().toISOString().slice(0, 10);
      const max = Math.max(1, Math.min(90, Number(this.maxDiasVisibles) || 21));
      let dates = [];
      if (r && r.desde && r.hasta) {
        const desdeStr = String(r.desde).slice(0, 10);
        const hastaStr = String(r.hasta).slice(0, 10);
        const visibleDesde = desdeStr < hoy ? hoy : desdeStr;
        if (hastaStr >= visibleDesde) {
          const d = new Date(visibleDesde + "T12:00:00");
          const end = new Date(hastaStr + "T12:00:00");
          if (!isNaN(d.getTime()) && !isNaN(end.getTime())) {
            for (; d <= end; d.setDate(d.getDate() + 1)) {
              dates.push(d.toISOString().slice(0, 10));
            }
          }
        }
      } else {
        const fechas = this.fechas || [];
        dates = fechas.filter((f) => String(f).slice(0, 10) >= hoy);
      }
      return dates.slice(0, max);
    },
    colaboradoresConDiasLocal() {
      const fechas = this.fechasVisibles;
      const periodos = this.periodosAusencia || [];
      const cols = this.colaboradores || [];
      return cols.map((c) => {
        const dias = {};
        fechas.forEach((fecha) => {
          const period = periodos.find(
            (p) =>
              Number(p.colleague_id) === Number(c.id) &&
              p.desde <= fecha &&
              p.hasta >= fecha
          );
          dias[fecha] = period ? period.tipo : (c.dias && c.dias[fecha]) || null;
        });
        // backup_display desde el periodo que toca en el rango visible
        const primeraFecha = fechas[0];
        const ultimaFecha = fechas[fechas.length - 1];
        const periodEnRango =
          primeraFecha && ultimaFecha
            ? periodos.find(
              (p) =>
                Number(p.colleague_id) === Number(c.id) &&
                p.desde <= ultimaFecha &&
                p.hasta >= primeraFecha
            )
            : null;
        let backup_display = c.backup_proyecto || "";
        if (periodEnRango && periodEnRango.backup_ids && periodEnRango.backup_ids.length) {
          const nombres = periodEnRango.backup_ids
            .map((bid) => cols.find((x) => Number(x.id) === Number(bid)))
            .filter(Boolean)
            .map((x) => x.nombre || [x.name, x.surname].filter(Boolean).join(" "));
          backup_display = nombres.join(", ");
        }
        if (periodEnRango && periodEnRango.project_code && String(periodEnRango.project_code).trim()) {
          const proy = String(periodEnRango.project_code).trim();
          backup_display = backup_display ? `${backup_display} · ${proy}` : proy;
        }
        return { ...c, dias, backup_display };
      });
    },
    colaboradoresFiltradosOrdenados() {
      const conDias = this.colaboradoresConDiasLocal || [];
      let list = conDias
        .filter((c) => this.esVigente(c))
        .filter((c) => this.tieneAlgunDiaFueraEnRango(c));
      const texto = (this.filtroTexto || "").toLowerCase().trim();
      if (texto) {
        list = list.filter(
          (c) =>
            (c.nombre && c.nombre.toLowerCase().includes(texto)) ||
            (c.area && c.area.toLowerCase().includes(texto)) ||
            (c.equipo && c.equipo.toLowerCase().includes(texto)) ||
            ((c.backup_proyecto || "").toLowerCase().includes(texto)) ||
            ((c.backup_display || "").toLowerCase().includes(texto))
        );
      }
      list = [...list].sort((a, b) => {
        const tieneFueraA = this.tieneAlgunDiaFuera(a);
        const tieneFueraB = this.tieneAlgunDiaFuera(b);
        if (tieneFueraA && !tieneFueraB) return -1;
        if (!tieneFueraA && tieneFueraB) return 1;
        if ((a.area || "") !== (b.area || "")) return (a.area || "").localeCompare(b.area || "");
        if ((a.equipo || "") !== (b.equipo || "")) return (a.equipo || "").localeCompare(b.equipo || "");
        return (a.nombre || "").localeCompare(b.nombre || "");
      });
      return list;
    },
    rangoTexto() {
      const f = this.fechasVisibles || [];
      if (!f.length) return "Sin rango";
      const ini = this.formatearFechaCorta(f[0]);
      const fin = this.formatearFechaCorta(f[f.length - 1]);
      return ini && fin ? `${ini} - ${fin}` : "Sin rango";
    },
  },
  created() {
    this.cargarDatos();
  },
  methods: {
    tieneRol(roles = []) {
      const u = this.usuario;
      if (!u) return false;
      const role = String(u.role || u.rol || "").toLowerCase().trim();
      const rolesNorm = (roles || []).map((r) => String(r).toLowerCase().trim());
      return rolesNorm.includes(role);
    },
    tieneAlgunDiaFuera(c) {
      if (!c.dias) return false;
      return Object.values(c.dias).some((v) => v === "VA" || v === "TE" || v === "LM" || v === "TT");
    },
    tieneAlgunDiaFueraEnRango(c) {
      const fechas = this.fechasVisibles || [];
      if (!c.dias || !fechas.length) return false;
      return fechas.some((f) => c.dias[f] === "VA" || c.dias[f] === "TE" || c.dias[f] === "LM" || c.dias[f] === "TT");
    },
    formatearFechaCorta(fechaStr) {
      if (fechaStr == null || String(fechaStr).trim() === "") return "";
      const d = new Date(String(fechaStr).trim() + "T12:00:00");
      if (isNaN(d.getTime())) return "";
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    esHoy(fechaStr) {
      const hoy = new Date().toISOString().slice(0, 10);
      return fechaStr === hoy;
    },
    esFinDeSemana(fechaStr) {
      if (!fechaStr) return false;
      const d = new Date(fechaStr + "T12:00:00");
      const day = d.getDay();
      return day === 0 || day === 6;
    },
    claseCelda(tipo) {
      if (!tipo) return "";
      if (tipo === "VA") return "cell-va";
      if (tipo === "TE") return "cell-te";
      if (tipo === "LM") return "cell-lm";
      if (tipo === "TT") return "cell-tt";
      return "";
    },
    tituloCelda(tipo, fecha) {
      const f = this.formatearFechaCorta(fecha);
      if (tipo === "VA") return `Vacaciones - ${f}`;
      if (tipo === "TE") return `Terreno - ${f}`;
      if (tipo === "LM") return `Licencia Médica - ${f}`;
      if (tipo === "TT") return `TeleTrabajo - ${f}`;
      return f;
    },
    truncarBackupIds() {
      if (Array.isArray(this.formBackupIds) && this.formBackupIds.length > 3) {
        this.formBackupIds = this.formBackupIds.slice(0, 3);
      }
    },
    backupDisplay(c) {
      return c.backup_display != null && c.backup_display !== "" ? c.backup_display : (c.backup_proyecto || "");
    },
    async exportarExcel() {
      const cols = this.colaboradoresFiltradosOrdenados || [];
      const fechas = this.fechasVisibles || [];
      if (!cols.length || !fechas.length) return;
      const border = { style: "thin", color: { argb: "FFE0E0E0" } };
      const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } };
      const headerFont = { bold: true, color: { argb: "FF374151" }, size: 11 };
      const weekendHeaderFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF2F2" } };
      const todayHeaderFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5EC88" } };
      const weekendCellFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF6CCC6" } };
      const fills = {
        VA: { type: "pattern", pattern: "solid", fgColor: { argb: "FFB91C1C" } },
        TE: { type: "pattern", pattern: "solid", fgColor: { argb: "FF15803D" } },
        LM: { type: "pattern", pattern: "solid", fgColor: { argb: "FF7B7B7B" } },
        TT: { type: "pattern", pattern: "solid", fgColor: { argb: "FFEAB308" } },
      };
      const fontColors = {
        VA: { argb: "FFFFFFFF" },
        TE: { argb: "FFFFFFFF" },
        LM: { argb: "FFFFFFFF" },
        TT: { argb: "FF1F2937" },
      };
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Disponibilidad", { views: [{ state: "frozen", ySplit: 1 }] });
      ws.columns = [
        { width: 18 },
        { width: 28 },
        { width: 32 },
        ...fechas.map(() => ({ width: 8 })),
      ];
      const headerRow = ["Área", "Nombre", "Backup / Proyecto"].concat(
        fechas.map((f) => this.formatearFechaCorta(f))
      );
      ws.addRow(headerRow);
      const headerRowObj = ws.getRow(1);
      headerRowObj.font = headerFont;
      headerRowObj.alignment = { vertical: "middle", horizontal: "left" };
      headerRowObj.height = 22;
      for (let col = 1; col <= headerRow.length; col++) {
        const cell = headerRowObj.getCell(col);
        cell.border = { top: border, left: border, bottom: border, right: border };
        const fechaIdx = col - 3;
        if (fechaIdx >= 0 && fechaIdx < fechas.length) {
          const f = fechas[fechaIdx];
          if (this.esHoy(f)) cell.fill = todayHeaderFill;
          else if (this.esFinDeSemana(f)) cell.fill = weekendHeaderFill;
          else cell.fill = headerFill;
          cell.alignment = { vertical: "middle", horizontal: "center" };
        } else {
          cell.fill = headerFill;
        }
      }
      cols.forEach((c, rowIdx) => {
        const row = [c.area || "", c.nombre || "", this.backupDisplay(c) || ""].concat(
          fechas.map((f) => (c.dias && c.dias[f]) ? c.dias[f] : "")
        );
        ws.addRow(row);
        const rowObj = ws.getRow(rowIdx + 2);
        rowObj.height = 20;
        rowObj.alignment = { vertical: "middle" };
        for (let col = 1; col <= row.length; col++) {
          const cell = rowObj.getCell(col);
          cell.border = { top: border, left: border, bottom: border, right: border };
          const fechaIdx = col - 3;
          if (fechaIdx >= 0 && fechaIdx < fechas.length) {
            const valor = row[col - 1];
            const f = fechas[fechaIdx];
            cell.alignment = { vertical: "middle", horizontal: "center" };
            if (valor && (valor === "VA" || valor === "TE" || valor === "LM" || valor === "TT")) {
              cell.fill = fills[valor];
              cell.font = { bold: true, color: fontColors[valor], size: 10 };
            } else {
              if (this.esFinDeSemana(f)) cell.fill = weekendCellFill;
              cell.font = { bold: true, color: { argb: "FF1F2937" }, size: 10 };
            }
          } else {
            cell.font = { size: 10, color: { argb: "FF1F2937" } };
            cell.alignment = { vertical: "middle", horizontal: "left" };
          }
        }
      });
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const fileName = `disponibilidad_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    async fetchProyectos() {
      this.loadingProyectos = true;
      this.proyectos = [];
      try {
        const { data } = await axios.get(`${API_BASE}/disponibilidad/projects`);
        this.proyectos = Array.isArray(data?.projects) ? data.projects : [];
      } catch (e) {
        this.proyectos = [];
      } finally {
        this.loadingProyectos = false;
      }
    },
    async abrirRegistrarAusencia() {
      this.editingPeriodIndex = null;
      this.formColaboradorId = null;
      this.formMotivo = null;
      this.formBackupIds = [];
      this.formProyectoId = null;
      this.formProyectoTexto = "";
      const hoy = new Date().toISOString().slice(0, 10);
      this.formDesde = hoy;
      this.formHasta = hoy;
      this.errorForm = "";
      await this.fetchProyectos();
      this.dialogAusencia = true;
    },
    async abrirEditarAusencia(c) {
      const fechas = this.fechasVisibles || [];
      const hoy = new Date().toISOString().slice(0, 10);
      const primera = fechas[0] || hoy;
      const ultima = fechas[fechas.length - 1] || hoy;
      let indexToEdit = null;
      for (let i = 0; i < this.periodosAusencia.length; i++) {
        const p = this.periodosAusencia[i];
        if (Number(p.colleague_id) !== Number(c.id)) continue;
        if (p.desde <= ultima && p.hasta >= primera) {
          indexToEdit = i;
          break;
        }
      }
      if (indexToEdit === null) {
        this.editingPeriodIndex = null;
        this.formColaboradorId = c.id;
        this.formDesde = primera;
        this.formHasta = ultima;
        this.formMotivo = null;
        this.formBackupIds = [];
        this.formProyectoId = null;
        this.formProyectoTexto = "";
      } else {
        const p = this.periodosAusencia[indexToEdit];
        this.editingPeriodIndex = indexToEdit;
        this.formColaboradorId = p.colleague_id;
        this.formDesde = p.desde ? String(p.desde).slice(0, 10) : p.desde;
        this.formHasta = p.hasta ? String(p.hasta).slice(0, 10) : p.hasta;
        this.formMotivo = p.tipo;
        this.formBackupIds = Array.isArray(p.backup_ids) ? p.backup_ids.map((id) => Number(id)) : [];
        if (p.project_id != null && Number(p.project_id) > 0) {
          this.formProyectoId = Number(p.project_id);
          this.formProyectoTexto = "";
        } else {
          this.formProyectoId = null;
          this.formProyectoTexto = (p.project_code && String(p.project_code).trim()) ? String(p.project_code).trim() : "";
        }
      }
      this.errorForm = "";
      await this.fetchProyectos();
      this.dialogAusencia = true;
    },
    esVigente(c) {
      const v = String(c?.vigente || c?.status || "").toLowerCase().trim();
      // backend suele enviar "Vigente" / "No Vigente" (o status = activo)
      return v === "vigente" || v === "activo" || v === "active";
    },
    async eliminarPeriodoAusencia() {
      if (this.editingPeriodIndex === null) return;
      const periodo = this.periodosAusencia[this.editingPeriodIndex];
      const id = periodo && periodo.id;
      if (id == null) return;
      this.guardando = true;
      this.cerrarDialogAusencia();
      try {
        await axios.delete(`${API_BASE}/disponibilidad/periodo/${id}`);
        await this.cargarDatos();
      } catch (err) {
        this.error = err.response?.data?.error || "Error al eliminar el periodo.";
      } finally {
        this.guardando = false;
      }
    },
    cerrarDialogAusencia() {
      this.dialogAusencia = false;
      this.errorForm = "";
      this.editingPeriodIndex = null;
      this.formColaboradorId = null;
      this.formMotivo = null;
      this.formBackupIds = [];
      this.formProyectoId = null;
      this.formProyectoTexto = "";
      const hoy = new Date().toISOString().slice(0, 10);
      this.formDesde = hoy;
      this.formHasta = hoy;
    },
    async guardarAusencia() {
      this.errorForm = "";
      if (!this.formColaboradorId) {
        this.errorForm = "Selecciona un colaborador.";
        return;
      }
      if (!this.formDesde || !this.formHasta) {
        this.errorForm = "Selecciona el periodo (desde y hasta).";
        return;
      }
      if (!this.formMotivo) {
        this.errorForm = "Selecciona el motivo de ausencia.";
        return;
      }
      const desde = new Date(this.formDesde);
      const hasta = new Date(this.formHasta);
      if (hasta < desde) {
        this.errorForm = "La fecha Hasta debe ser mayor o igual que Desde.";
        return;
      }
      const payload = {
        colleague_id: this.formColaboradorId,
        desde: String(this.formDesde).slice(0, 10),
        hasta: String(this.formHasta).slice(0, 10),
        tipo: this.formMotivo,
        backup_ids: (this.formBackupIds || []).slice(0, 3).map((id) => Number(id)),
        project_id: this.formProyectoId != null && Number(this.formProyectoId) > 0 ? Number(this.formProyectoId) : null,
      };
      const textoProyecto = (this.formProyectoTexto || "").trim();
      if (payload.project_id == null && textoProyecto) {
        payload.project_code = textoProyecto;
      }
      const editingId = this.editingPeriodIndex !== null ? this.periodosAusencia[this.editingPeriodIndex]?.id : null;
      this.guardando = true;
      try {
        if (editingId != null) {
          await axios.put(`${API_BASE}/disponibilidad/periodo/${editingId}`, payload);
        } else {
          await axios.post(`${API_BASE}/disponibilidad/periodo`, payload);
        }
        await this.cargarDatos();
        this.cerrarDialogAusencia();
      } catch (err) {
        const status = err.response?.status;
        const msg = err.response?.data?.error;
        if (status === 404) {
          this.errorForm = msg || "No se encontró la ruta del servidor. Comprueba que el backend esté en marcha (puerto 5001) y reiniciado.";
        } else {
          this.errorForm = msg || "Error al guardar el periodo.";
        }
      } finally {
        this.guardando = false;
      }
    },
    async cargarDatos() {
      this.loading = true;
      this.error = "";
      try {
        // Rango amplio para que el backend devuelva fechas suficientes; el backend SIEMPRE devuelve todos los periodos
        const hoy = new Date();
        const desde = new Date(hoy);
        desde.setDate(desde.getDate() - 30);
        const hasta = new Date(hoy);
        hasta.setDate(hasta.getDate() + 90);
        const desdeStr = desde.toISOString().slice(0, 10);
        const hastaStr = hasta.toISOString().slice(0, 10);
        const { data } = await axios.get(`${API_BASE}/disponibilidad`, {
          params: { desde: desdeStr, hasta: hastaStr, _t: Date.now() },
        });
        this.colaboradores = data.colaboradores || [];
        this.fechas = data.fechas || data.fechasVisibles || [];
        const rawPeriodos = Array.isArray(data.periodos)
          ? data.periodos
          : Array.isArray(data.periodosAusencia)
            ? data.periodosAusencia
            : [];

        this.periodosAusencia = rawPeriodos.map((p) => ({
          ...p,
          desde: p.desde ? String(p.desde).slice(0, 10) : p.desde,
          hasta: p.hasta ? String(p.hasta).slice(0, 10) : p.hasta,
        }));
      } catch (err) {
        this.error = err.response?.data?.error || err.response?.data?.message || err.message || "Error al cargar la disponibilidad.";
        this.colaboradores = [];
        this.fechas = [];
        this.periodosAusencia = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Card crece con el contenido; si supera el alto de la página, el área de la tabla hace scroll */
.disponibilidad-page {
  max-width: 100%;
}

/* La tarjeta crece con el contenido; no supera la vista y luego la tabla hace scroll */
.availability-card {
  border-radius: 12px;
  overflow: hidden;
  max-height: calc(100vh - 64px - 24px);
  align-self: flex-start;
  width: 100%;
}

.availability-card .table-card-text {
  min-height: 0;
  flex: 1 1 auto;
}

.table-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.availability-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  min-width: 640px;
}

.availability-table th,
.availability-table td {
  border: 1px solid #e0e0e0;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
}

.availability-table thead th {
  background: #f5f5f5;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
}

.availability-table thead th.col-finde {
  background-color: #fef2f2;
}

.availability-table tbody tr {
  height: 52px;
}

.availability-table tbody tr:hover {
  background: #fafafa;
}

.row-clickable {
  cursor: pointer;
}

.col-area {
  min-width: 100px;
}

.col-nombre {
  min-width: 140px;
}

.col-backup {
  min-width: 220px;
  max-width: 280px;
}

.col-fecha {
  min-width: 42px;
  max-width: 52px;
  text-align: center;
  padding: 6px 4px !important;
}

.fecha-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(-180deg);
  display: inline-block;
  font-size: 0.75rem;
}

.fecha-hoy {
  background: #f5ec88 !important;
}

/* Sábado y domingo: fondo rojito para diferenciar de días laborales */
.col-finde {
  background-color: #f6ccc6;
}

.cell-day {
  font-weight: 600;
  color: #1f2937;
}

.cell-va {
  background: #b91c1c;
  color: #fff;
}

.cell-te {
  background: #15803d;
  color: #fff;
}

.cell-lm {
  background: #7b7b7b;
  color: #fff;
}

.cell-tt {
  background: #eab308;
  color: #1f2937;
}

.legend {
  border-top: 1px solid #1a62c0;
  font-size: 0.85rem;
}

.legend-title {
  font-weight: 600;
  margin-right: 12px;
}

.legend-item {
  margin-right: 16px;
}

.legend-item.va {
  color: #b91c1c;
  font-weight: 500;
}

.legend-item.te {
  color: #15803d;
  font-weight: 500;
}

.legend-item.lm {
  color: #7b7b7b;
  font-weight: 500;
}

.legend-item.tt {
  color: #b45309;
  font-weight: 500;
}
.motivo-color-VA{
  color: #b91c1c;
  font-weight: 500;
}

.motivo-color-LM {
  color: #7b7b7b;
  font-weight: 500;
}

.motivo-color-TE {
  color: #15803d;
  font-weight: 500;
}

.motivo-color-TT {
  color: #b45309;
  font-weight: 500;
}
</style>
